import { apiClient } from '@shared/api/client';
import { ApiResponse } from '@shared/api';
import {
  SurveyReportDTO,
  CreateSurveyReportDTO,
  UpdateSurveyReportDTO,
  SurveyReportListDTO,
  PdfExportDTO,
  PdfExportListDTO,
  ReportStatisticsDTO,
  ReportPermissionsDTO,
  ReportPreviewDTO,
} from '../types';

export const surveyReportApi = {
  // Report generation (simplified - synchronous)
  async generateFromAudit(
    auditId: string,
    data?: {
      title?: string;
      description?: string;
      options?: any;
    }
  ): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.post<SurveyReportDTO>(`/survey-audits/${auditId}/generate-report`, data);
  },

  // Basic CRUD operations
  async create(data: CreateSurveyReportDTO): Promise<SurveyReportDTO> {
    const response = await apiClient.post<SurveyReportDTO>('/survey-reports', data);
    return response.data!;
  },

  async createReport(data: CreateSurveyReportDTO): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.post<SurveyReportDTO>('/survey-reports', data);
  },

  async getSurveyReport(reportId: string): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.get<SurveyReportDTO>(`/survey-reports/${reportId}`);
  },

  async update(id: string, data: UpdateSurveyReportDTO): Promise<SurveyReportDTO> {
    const response = await apiClient.put<SurveyReportDTO>(`/survey-reports/${id}`, data);
    return response.data!;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/survey-reports/${id}`);
  },

  async getBySubjectId(subjectId: string): Promise<SurveyReportListDTO> {
    const response = await apiClient.get<SurveyReportListDTO>(`/subjects/${subjectId}/reports`);
    return response.data!;
  },

  async updateReport(
    reportId: string,
    data: UpdateSurveyReportDTO
  ): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.put<SurveyReportDTO>(`/survey-reports/${reportId}`, data);
  },

  async deleteReport(reportId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/survey-reports/${reportId}`);
  },

  async getAllReports(): Promise<ApiResponse<SurveyReportDTO[]>> {
    return apiClient.get<SurveyReportDTO[]>('/survey-reports');
  },

  // Note: publishReport removed - no publish workflow

  async getReportPreview(reportId: string): Promise<ApiResponse<ReportPreviewDTO>> {
    return apiClient.get<ReportPreviewDTO>(`/survey-reports/${reportId}/preview`);
  },

  // Access by different identifiers
  async getReportByInstanceId(instanceId: string): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.get<SurveyReportDTO>(`/survey-instances/${instanceId}/report`);
  },

  async getReportByAuditId(auditId: string): Promise<ApiResponse<SurveyReportDTO>> {
    return apiClient.get<SurveyReportDTO>(`/survey-audits/${auditId}/report`);
  },

  async getReportsBySurveyId(surveyId: string): Promise<ApiResponse<SurveyReportDTO[]>> {
    return apiClient.get<SurveyReportDTO[]>(`/surveys/${surveyId}/reports`);
  },

  async getReportsBySubjectId(subjectId: string): Promise<ApiResponse<SurveyReportListDTO>> {
    return apiClient.get<SurveyReportListDTO>(`/subjects/${subjectId}/reports`);
  },

  async getReportsByTenantId(
    tenantId: string,
    limit?: number
  ): Promise<ApiResponse<SurveyReportListDTO>> {
    const params = limit ? `?limit=${limit}` : '';
    return apiClient.get<SurveyReportListDTO>(`/tenants/${tenantId}/reports${params}`);
  },

  // PDF Export operations
  async generatePdfExport(
    reportId: string,
    options?: {
      includeComments?: boolean;
      includeStatistics?: boolean;
      format?: 'A4' | 'LETTER';
      orientation?: 'portrait' | 'landscape';
      includeRawData?: boolean;
    }
  ): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`/survey-reports/${reportId}/export`, { options });
  },

  async downloadPdfExport(
    reportId: string,
    exportId: string,
    disposition: 'attachment' | 'inline' = 'attachment'
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    const query = disposition === 'inline' ? '?disposition=inline' : '';
    return apiClient.get<{ downloadUrl: string }>(
      `/survey-reports/${reportId}/export/${exportId}/download${query}`
    );
  },

  async listPdfExports(reportId: string): Promise<ApiResponse<PdfExportListDTO>> {
    return apiClient.get<PdfExportListDTO>(`/survey-reports/${reportId}/exports`);
  },

  async deletePdfExport(reportId: string, exportId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/survey-reports/${reportId}/exports/${exportId}`);
  },

  // Statistics and analytics
  async getReportStatistics(reportId: string): Promise<ApiResponse<ReportStatisticsDTO>> {
    return apiClient.get<ReportStatisticsDTO>(`/survey-reports/${reportId}/statistics`);
  },

  async getSubjectReportSummary(subjectId: string): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/subjects/${subjectId}/report-summary`);
  },

  // Permissions
  async checkReportPermissions(reportId: string): Promise<ApiResponse<ReportPermissionsDTO>> {
    return apiClient.get<ReportPermissionsDTO>(`/survey-reports/${reportId}/permissions`);
  },

  // Bulk operations (publishMultipleReports removed - no publish workflow)
  async deleteMultipleReports(reportIds: string[]): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/survey-reports/bulk/delete', { reportIds });
  },

  async exportMultipleReports(reportIds: string[]): Promise<ApiResponse<PdfExportDTO[]>> {
    return apiClient.post<PdfExportDTO[]>('/survey-reports/bulk/export', { reportIds });
  },

  // Health and validation
  async validateReportData(
    reportId: string
  ): Promise<ApiResponse<{ isValid: boolean; issues: string[] }>> {
    return apiClient.get<{ isValid: boolean; issues: string[] }>(
      `/survey-reports/${reportId}/validate`
    );
  },

  async validateReportIntegrity(
    reportId: string
  ): Promise<ApiResponse<{ isHealthy: boolean; issues: string[] }>> {
    return apiClient.get<{ isHealthy: boolean; issues: string[] }>(
      `/survey-reports/${reportId}/health`
    );
  },
};

// Re-export DTOs for use in other modules
export type { CreateSurveyReportDTO, UpdateSurveyReportDTO };
